import { useState, useEffect, useCallback } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Bell, Plus, Trash2, Pencil, Check, X, Target, AlertTriangle, Clock, ListChecks, Users } from "lucide-react";
import { ConfirmDeleteDialog } from "@/components/ConfirmDeleteDialog";
import axios from "axios";
import { useAuth } from "@/contexts/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const triggerTypes = [
  { value: "goals_stale", label: "Goal Updates Stale", icon: Target, desc: "No goal update in X days", color: "text-purple-500" },
  { value: "milestone_approaching", label: "Milestone Approaching", icon: Clock, desc: "Milestone due within X days", color: "text-blue-500" },
  { value: "painpoints_unresolved", label: "Pain Points Unresolved", icon: AlertTriangle, desc: "Open pain points older than X days", color: "text-amber-500" },
  { value: "task_overdue", label: "Task Overdue", icon: ListChecks, desc: "Tasks past due date by X days", color: "text-red-500" },
];

const getTriggerInfo = (type) => triggerTypes.find((t) => t.value === type) || triggerTypes[0];

export const NotificationRulesSection = () => {
  const { user } = useAuth();
  const [rules, setRules] = useState([]);
  const [users, setUsers] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [form, setForm] = useState({
    name: "",
    trigger_type: "goals_stale",
    days_threshold: 7,
    target_user_ids: [],
    product_id: "",
    enabled: true,
    show_popup: true,
  });

  const fetchData = useCallback(async () => {
    try {
      const [rulesRes, usersRes, productsRes] = await Promise.all([
        axios.get(`${API}/notification-rules`),
        axios.get(`${API}/admin/users`),
        axios.get(`${API}/admin/products`),
      ]);
      setRules(rulesRes.data);
      setUsers(usersRes.data);
      setProducts(productsRes.data);
    } catch (err) { console.error(err); }
    setLoading(false);
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  if (user?.role !== "admin") return null;

  const resetForm = () => {
    setForm({ name: "", trigger_type: "goals_stale", days_threshold: 7, target_user_ids: [], product_id: "", enabled: true, show_popup: true });
    setShowForm(false);
    setEditingId(null);
  };

  const handleSave = async () => {
    if (!form.name.trim() || form.target_user_ids.length === 0) return;
    try {
      if (editingId) {
        await axios.patch(`${API}/notification-rules/${editingId}`, form);
      } else {
        await axios.post(`${API}/notification-rules`, form);
      }
      resetForm();
      await fetchData();
    } catch (err) { console.error(err); }
  };

  const startEdit = (rule) => {
    setEditingId(rule.id);
    setForm({
      name: rule.name,
      trigger_type: rule.trigger_type,
      days_threshold: rule.days_threshold,
      target_user_ids: rule.target_user_ids || [],
      product_id: rule.product_id || "",
      enabled: rule.enabled,
      show_popup: rule.show_popup,
    });
    setShowForm(true);
  };

  const handleDelete = async (ruleId) => {
    try {
      await axios.delete(`${API}/notification-rules/${ruleId}`);
      await fetchData();
    } catch (err) { console.error(err); }
  };

  const toggleEnabled = async (rule) => {
    try {
      await axios.patch(`${API}/notification-rules/${rule.id}`, { enabled: !rule.enabled });
      await fetchData();
    } catch (err) { console.error(err); }
  };

  const toggleUser = (userId) => {
    setForm((prev) => ({
      ...prev,
      target_user_ids: prev.target_user_ids.includes(userId)
        ? prev.target_user_ids.filter((id) => id !== userId)
        : [...prev.target_user_ids, userId],
    }));
  };

  const getUserName = (uid) => users.find((u) => u.id === uid)?.name || uid;
  const getProductName = (pid) => products.find((p) => p.id === pid)?.name || "All Products";

  if (loading) return null;

  return (
    <>
    <Card className="card-modern" data-testid="notification-rules-section">
      <CardContent className="p-6">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-rose-400 to-rose-500 flex items-center justify-center">
              <Bell className="w-5 h-5 text-white" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-slate-900 dark:text-slate-100">Notification Rules</h2>
              <p className="text-sm text-slate-500 dark:text-slate-400">Set automated popup & bell notifications</p>
            </div>
          </div>
          {!showForm && (
            <Button
              onClick={() => { resetForm(); setShowForm(true); }}
              className="bg-[#00D4C8] hover:bg-[#00B8AE] text-white font-semibold"
              data-testid="add-rule-btn"
            >
              <Plus className="w-4 h-4 mr-2" /> Add Rule
            </Button>
          )}
        </div>

        {/* Rule Form */}
        {showForm && (
          <div className="mb-6 p-5 rounded-xl border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/50 space-y-4" data-testid="rule-form">
            <div className="flex items-center justify-between">
              <h3 className="text-sm font-semibold text-slate-700 dark:text-slate-200">{editingId ? "Edit Rule" : "New Notification Rule"}</h3>
              <button onClick={resetForm} className="text-slate-400 hover:text-slate-600 dark:hover:text-slate-200"><X className="w-4 h-4" /></button>
            </div>

            <div>
              <label className="text-xs font-semibold text-slate-500 dark:text-slate-400 mb-1 block uppercase tracking-wider">Rule Name</label>
              <Input
                value={form.name}
                onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))}
                placeholder="e.g., Remind PMs to update goals"
                className="dark:bg-slate-800 dark:border-slate-600 dark:text-slate-100"
                data-testid="rule-name-input"
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="text-xs font-semibold text-slate-500 dark:text-slate-400 mb-1 block uppercase tracking-wider">Trigger Type</label>
                <Select value={form.trigger_type} onValueChange={(v) => setForm((p) => ({ ...p, trigger_type: v }))}>
                  <SelectTrigger className="dark:bg-slate-800 dark:border-slate-600 dark:text-slate-100" data-testid="rule-trigger-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="dark:bg-slate-800 dark:border-slate-700">
                    {triggerTypes.map((t) => (
                      <SelectItem key={t.value} value={t.value} className="dark:text-slate-100">
                        <span className="flex items-center gap-2"><t.icon className={`w-3.5 h-3.5 ${t.color}`} />{t.label}</span>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-[11px] text-slate-400 mt-1">{getTriggerInfo(form.trigger_type).desc}</p>
              </div>

              <div>
                <label className="text-xs font-semibold text-slate-500 dark:text-slate-400 mb-1 block uppercase tracking-wider">Days Threshold</label>
                <Input
                  type="number"
                  min={1}
                  max={365}
                  value={form.days_threshold}
                  onChange={(e) => setForm((p) => ({ ...p, days_threshold: parseInt(e.target.value) || 1 }))}
                  className="dark:bg-slate-800 dark:border-slate-600 dark:text-slate-100"
                  data-testid="rule-days-input"
                />
              </div>

              <div>
                <label className="text-xs font-semibold text-slate-500 dark:text-slate-400 mb-1 block uppercase tracking-wider">Product Scope</label>
                <Select value={form.product_id || "_all"} onValueChange={(v) => setForm((p) => ({ ...p, product_id: v === "_all" ? "" : v }))}>
                  <SelectTrigger className="dark:bg-slate-800 dark:border-slate-600 dark:text-slate-100" data-testid="rule-product-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="dark:bg-slate-800 dark:border-slate-700">
                    <SelectItem value="_all" className="dark:text-slate-100">All Products</SelectItem>
                    {products.map((p) => (
                      <SelectItem key={p.id} value={p.id} className="dark:text-slate-100">{p.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Target Users */}
            <div>
              <label className="text-xs font-semibold text-slate-500 dark:text-slate-400 mb-2 block uppercase tracking-wider flex items-center gap-1.5">
                <Users className="w-3.5 h-3.5" /> Target Users
              </label>
              <div className="flex flex-wrap gap-2">
                {users.map((u) => {
                  const isSelected = form.target_user_ids.includes(u.id);
                  return (
                    <button
                      key={u.id}
                      onClick={() => toggleUser(u.id)}
                      className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-all ${
                        isSelected
                          ? "bg-[#00D4C8]/15 border-[#00D4C8] text-[#00B8AE] dark:text-[#00D4C8]"
                          : "bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-600 text-slate-500 dark:text-slate-400 hover:border-slate-300"
                      }`}
                      data-testid={`rule-user-${u.id}`}
                    >
                      {isSelected && <Check className="w-3 h-3 inline mr-1" />}
                      {u.name}
                      <span className="ml-1 opacity-50 text-[10px]">({u.role === "admin" ? "Admin" : u.role === "product_manager" ? "PO" : "TM"})</span>
                    </button>
                  );
                })}
              </div>
              {form.target_user_ids.length === 0 && (
                <p className="text-[11px] text-amber-500 mt-1">Select at least one user to receive this notification</p>
              )}
            </div>

            {/* Options row */}
            <div className="flex items-center gap-6">
              <label className="flex items-center gap-2 text-sm text-slate-600 dark:text-slate-300 cursor-pointer">
                <Switch checked={form.show_popup} onCheckedChange={(v) => setForm((p) => ({ ...p, show_popup: v }))} data-testid="rule-popup-toggle" />
                Show as popup modal
              </label>
              <label className="flex items-center gap-2 text-sm text-slate-600 dark:text-slate-300 cursor-pointer">
                <Switch checked={form.enabled} onCheckedChange={(v) => setForm((p) => ({ ...p, enabled: v }))} data-testid="rule-enabled-toggle" />
                Enabled
              </label>
            </div>

            <div className="flex gap-2 pt-1">
              <Button onClick={handleSave} disabled={!form.name.trim() || form.target_user_ids.length === 0} className="bg-[#00D4C8] hover:bg-[#00B8AE] text-white font-semibold" data-testid="save-rule-btn">
                <Check className="w-4 h-4 mr-1" /> {editingId ? "Update Rule" : "Create Rule"}
              </Button>
              <Button variant="outline" onClick={resetForm} className="dark:border-slate-600 dark:text-slate-300">Cancel</Button>
            </div>
          </div>
        )}

        {/* Rules List */}
        <div className="space-y-3" data-testid="rules-list">
          {rules.length === 0 && !showForm && (
            <div className="text-center py-8 text-slate-400 dark:text-slate-500">
              <Bell className="w-8 h-8 mx-auto mb-2 opacity-40" />
              <p className="text-sm">No notification rules yet. Create one to get started.</p>
            </div>
          )}
          {rules.map((rule) => {
            const info = getTriggerInfo(rule.trigger_type);
            const Icon = info.icon;
            return (
              <div
                key={rule.id}
                className={`group flex items-center gap-4 p-4 rounded-xl border transition-all ${
                  rule.enabled
                    ? "border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800/40"
                    : "border-slate-100 dark:border-slate-800 bg-slate-50 dark:bg-slate-900/30 opacity-60"
                }`}
                data-testid={`rule-card-${rule.id}`}
              >
                <div className={`w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 ${rule.enabled ? "bg-slate-100 dark:bg-slate-700" : "bg-slate-100 dark:bg-slate-800"}`}>
                  <Icon className={`w-4 h-4 ${info.color}`} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-0.5">
                    <h4 className="text-sm font-semibold text-slate-800 dark:text-slate-100 truncate">{rule.name}</h4>
                    {rule.show_popup && <Badge className="bg-rose-50 dark:bg-rose-900/30 text-rose-600 dark:text-rose-400 border-0 text-[10px]">Popup</Badge>}
                    {!rule.enabled && <Badge className="bg-slate-100 dark:bg-slate-700 text-slate-500 dark:text-slate-400 border-0 text-[10px]">Disabled</Badge>}
                  </div>
                  <div className="flex items-center gap-2 flex-wrap text-[11px] text-slate-500 dark:text-slate-400">
                    <span>{info.label}</span>
                    <span className="text-slate-300 dark:text-slate-600">|</span>
                    <span>{rule.days_threshold} days</span>
                    <span className="text-slate-300 dark:text-slate-600">|</span>
                    <span>{getProductName(rule.product_id)}</span>
                    <span className="text-slate-300 dark:text-slate-600">|</span>
                    <span>{(rule.target_user_ids || []).map(getUserName).join(", ")}</span>
                  </div>
                </div>
                <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                  <Switch checked={rule.enabled} onCheckedChange={() => toggleEnabled(rule)} data-testid={`rule-toggle-${rule.id}`} />
                  <button onClick={() => startEdit(rule)} className="p-1.5 rounded-md text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-700" data-testid={`rule-edit-${rule.id}`}>
                    <Pencil className="w-3.5 h-3.5" />
                  </button>
                  <button onClick={() => setDeleteTarget({ id: rule.id, label: rule.name || "this rule" })} className="p-1.5 rounded-md text-slate-400 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-900/30" data-testid={`rule-delete-${rule.id}`}>
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      </CardContent>
    </Card>
    <ConfirmDeleteDialog open={!!deleteTarget} onClose={() => setDeleteTarget(null)} onConfirm={() => handleDelete(deleteTarget?.id)} title="Delete Rule" description={`Are you sure you want to delete "${deleteTarget?.label}"? This action cannot be undone.`} />
    </>
  );
};
